import { makeStyles, tokens } from '@coreai/fluentui-react';
import { WarningRegular } from '@fluentui/react-icons';
import type { MonitoringDataError } from '@/types/monitoring';
import { visuallyHiddenStyle } from '@/styles/a11y';

const useStyles = makeStyles({
  status: visuallyHiddenStyle,
  error: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    marginInline: tokens.spacingHorizontalM,
    marginBlock: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderLeft: `${tokens.strokeWidthThick} solid ${tokens.colorStatusWarningBorder1}`,
    backgroundColor: tokens.colorStatusWarningBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  warning: {
    flexShrink: 0,
    color: tokens.colorStatusWarningForeground1,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
});

export const DataDetailsWarning = ({ label = 'Execution details unavailable' }: { label?: string }) => {
  const styles = useStyles();
  return <WarningRegular className={styles.warning} role="img" aria-label={label} />;
};

export const ToolDetailsWarning = () => <DataDetailsWarning label="Tool details unavailable" />;

interface DataDetailsErrorProps {
  message: string;
  label?: string | undefined;
  role?: 'alert' | 'note';
}

export const DataDetailsError = ({ message, label, role = 'alert' }: DataDetailsErrorProps) => {
  const styles = useStyles();
  return (
    <div className={styles.error} role={role} aria-label={label}>
      <WarningRegular className={styles.warning} aria-hidden />
      <span>{message}</span>
    </div>
  );
};

const getDataErrorContext = (error: MonitoringDataError) => {
  const messageAction = error.nodeId?.match(/^chatmsg-action-(\d+)-(.+)$/);
  const messageIndex = error.messageIndex ?? (messageAction ? Number(messageAction[1]) : undefined);
  return [
    messageAction?.[2] ?? error.nodeId?.replace(/^(action|trigger|tool)-/, ''),
    error.iterationName !== undefined
      ? `iteration ${/^\d+$/.test(error.iterationName) ? Number(error.iterationName) + 1 : error.iterationName}` : undefined,
    messageIndex !== undefined ? `message ${messageIndex + 1}` : undefined,
  ].filter(Boolean).join(', ');
};

export const DataDetailsStatus = ({ errors }: { errors: readonly MonitoringDataError[] }) => {
  const styles = useStyles();
  return (
    <div className={styles.status} role="status" aria-label="Monitoring data status" aria-live="polite" aria-atomic="true">
      {errors.map(error => {
        const context = getDataErrorContext(error);
        return context ? `${context}: ${error.message}` : error.message;
      }).join('. ')}
    </div>
  );
};

export const DataDetailsErrors = ({ errors, showContext = false, role = 'alert' }: {
  errors: readonly MonitoringDataError[];
  showContext?: boolean;
  role?: 'alert' | 'note';
}) => errors.map(error => {
  const context = getDataErrorContext(error);
  return (
    <DataDetailsError
      key={JSON.stringify(error)}
      role={role}
      message={showContext && context ? `${context}: ${error.message}` : error.message}
      label={context ? `${context}: ${error.message.toLowerCase()}` : error.message}
    />
  );
});
